import { Box, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatPriceDiff } from '@/utils/price.utils';

interface BoxChoiceCardProps {
  title: string;
  subtitle?: string | null;
  /** Extra sul capitolato (0 se capitolato). */
  priceDiff: number;
  /** true = opzione di capitolato (inclusa). */
  isDefault: boolean;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  height: '100%',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  border: `${selected ? 2 : 1}px solid`,
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  transition: 'border-color 200ms, box-shadow 200ms',
  '&:hover': { borderColor: theme.palette.primary.main },
}));

/** Card di scelta single-choice per il wizard Box (pacchetto basculante/ricarica o opzione unione). */
export function BoxChoiceCard({
  title,
  subtitle,
  priceDiff,
  isDefault,
  selected,
  disabled,
  onSelect,
}: BoxChoiceCardProps) {
  return (
    <CardRoot
      selected={selected}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={disabled ? { opacity: 0.6, cursor: 'default', '&:hover': { borderColor: 'divider' } } : {}}
    >
      {selected && (
        <CheckCircleIcon
          color="primary"
          fontSize="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
          aria-hidden="true"
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 3 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.25 }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      <Box sx={{ mt: 'auto', pt: 1 }}>
        {isDefault ? (
          <Chip size="small" label="Compreso nel capitolato" variant="outlined" />
        ) : (
          <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
            {formatPriceDiff(priceDiff)}
          </Typography>
        )}
      </Box>
    </CardRoot>
  );
}
